import React, { useState } from "react";
import PropTypes from "prop-types";
import { METRIC_GROUP_LABELS, METRIC_GROUP_ICONS } from "../../../../constants/performance";
import { AvatarPreview, NeoBadge, NeoButton, NeoEmptyState, NeoTable, NeoModal } from "../../../../components/Common";
import EmployeeEvolutionPanel from "./EmployeeEvolutionPanel";

/**
 * RankingTable — Tabela de ranking com painel de evolução em modal.
 * Usa NeoTable do design system com colunas declarativas.
 * A evolução histórica e os gráficos são exibidos em um modal premium.
 */
const RankingTable = ({ snapshots, excludedSnapshots = [], onViewDetail }) => {
    const [expandedEmployee, setExpandedEmployee] = useState(null);

    const getRoleBadgeColor = (role) => {
        const map = { professor: "info", consultor: "success", gerente: "warning" };
        return map[role] || "secondary";
    };

    const toggleExpand = (emp) => {
        setExpandedEmployee(prev => prev?.id === emp.id ? null : emp);
    };

    if (!snapshots || snapshots.length === 0) {
        return (
            <NeoEmptyState
                icon="mdi-trophy-broken"
                title="Nenhum resultado de performance encontrado"
                description="Selecione um período diferente ou recalcule os dados."
            />
        );
    }

    // Colunas declarativas para NeoTable
    const columns = [
        {
            key: "position",
            label: "Pos",
            width: "50px",
            align: "center",
            render: (_row, _rowIdx, meta) => {
                const globalIdx = (meta.page - 1) * meta.rowsPerPage + _rowIdx;
                return <span className="fw-bold">{globalIdx + 1}</span>;
            }
        },
        {
            key: "employeeName",
            label: "Colaborador",
            render: (row) => (
                <div className="d-flex align-items-center">
                    <AvatarPreview
                        src={row.employeePhotoUrl}
                        alt={row.employeeName}
                        size="sm"
                        className="me-3"
                    />
                    <div>
                        <span className="font-size-14 fw-bold d-block text-brand-primary">{row.employeeName}</span>
                        <small className="text-muted">{row.employeeRoleName}</small>
                    </div>
                </div>
            )
        },
        {
            key: "employeeRole",
            label: "Grupo",
            render: (row) => {
                const groupIcon = METRIC_GROUP_ICONS[row.employeeRole] || "mdi-account";
                return (
                    <NeoBadge color={getRoleBadgeColor(row.employeeRole)} icon={groupIcon}>
                        {METRIC_GROUP_LABELS[row.employeeRole] || row.employeeRole}
                    </NeoBadge>
                );
            }
        },
        {
            key: "scoreFinal",
            label: "Score",
            width: "80px",
            render: (row) => (
                <span className="neo-performance-score font-size-18">{row.scoreFinal?.toFixed(1)}</span>
            )
        },
        {
            key: "actions",
            label: "",
            width: "90px",
            align: "center",
            render: (row) => {
                return (
                    <div className="d-flex align-items-center justify-content-end gap-1">
                        <NeoButton
                            variant="soft-info"
                            size="sm"
                            square={true}
                            icon="mdi-chart-line"
                            onClick={(e) => { e.stopPropagation(); toggleExpand(row); }}
                            title="Ver Evolução Histórica"
                        />
                        <NeoButton
                            variant="soft-primary"
                            size="sm"
                            square
                            icon="mdi-eye-outline"
                            onClick={(e) => { e.stopPropagation(); onViewDetail?.(row); }}
                            title="Ver Detalhes"
                        />
                    </div>
                );
            }
        }
    ];

    return (
        <>
            <NeoTable
                columns={columns}
                data={snapshots}
                keyField="id"
                onRowClick={toggleExpand}
                defaultItemsPerPage={10}
                emptyMessage="Nenhum resultado encontrado"
                emptySubMessage="Tente ajustar os filtros de período ou grupo."
            />

            {/* Modal de Evolução Histórica */}
            <NeoModal
                isOpen={!!expandedEmployee}
                toggle={() => setExpandedEmployee(null)}
                size="lg"
                title={`Evolução Histórica — ${expandedEmployee?.employeeName || ""}`}
            >
                {expandedEmployee && (
                    <EmployeeEvolutionPanel employee={expandedEmployee} />
                )}
            </NeoModal>

            {/* Colaboradores excluídos por dados insuficientes */}
            {excludedSnapshots.length > 0 && (
                <div className="border-top mt-3 pt-3 pb-2">
                    <p className="text-muted font-size-13 mb-2">
                        <i className="mdi mdi-information-outline me-1"></i>
                        <strong>Fora do ranking</strong> — dados insuficientes para avaliação justa:
                    </p>
                    {excludedSnapshots.map(emp => (
                        <div key={emp.id} className="d-flex align-items-center mb-2 ms-2">
                            <AvatarPreview
                                src={emp.employeePhotoUrl}
                                alt={emp.employeeName}
                                size="xs"
                                className="me-2"
                            />
                            <span className="font-size-13 text-muted">
                                {emp.employeeName}
                                <span className="ms-1 font-size-12 text-warning">
                                    — {emp.insufficientDataReason}
                                </span>
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

RankingTable.propTypes = {
    snapshots: PropTypes.arrayOf(PropTypes.object),
    excludedSnapshots: PropTypes.arrayOf(PropTypes.object),
    onViewDetail: PropTypes.func,
};

export default RankingTable;
